import React, { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

import '../pdfjs-worker';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import { setCurrentPage, setNumPages, setPageInputValue } from '@/redux/CustomContracts/actions';
import { useDispatch, useSelector } from 'react-redux';

import SaralLoaderImage from '@/components/common-components/SaralLoaderImage';
import CalendarWithoutDates from '@/components/Icons/icons/CalendarWithoutDates';
import BriefcaseIcon from '@/components/Icons/icons/CustomContracts/BriefcaseIcon';
import BuildingIcon from '@/components/Icons/icons/CustomContracts/BuildingIcon';
import EmailIcon from '@/components/Icons/icons/CustomContracts/EmailIcon';
import FourHorizontalLines from '@/components/Icons/icons/CustomContracts/FourHorizontalLines';
import SignatureIcon from '@/components/Icons/icons/CustomContracts/SignatureIcon';
import UserIcon from '@/components/Icons/icons/CustomContracts/UserIcon';

import { RichTextEditorForContract } from './RichTextEditorForContract';

const getIconByKey = (iconKey) => {
  const iconMap = {
    user: <UserIcon />,
    email: <EmailIcon />,
    calendar: <CalendarWithoutDates color="var(--icon-default)" />,
    horizontalLines: <FourHorizontalLines />,
    building: <BuildingIcon />,
    briefcase: <BriefcaseIcon />,
    pencil: <SignatureIcon />,
  };
  return iconMap[iconKey] || <UserIcon />;
};

const DroppedField = ({
  field,
  scale,
  onDragEnd,
  onRemove,
  isActive,
  onSelect,
  onResize,
  updateTextFieldValue,
  onFieldChange,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const fieldRef = useRef(null);
  const initialMousePos = useRef({ x: 0, y: 0 });
  const initialWidth = useRef(0);
  const initialHeight = useRef(0);
  const textareaRef = useRef(null);
  const MIN_WIDTH = 150;
  const MAX_WIDTH = 400;
  const MIN_HEIGHT = 32;
  const MAX_HEIGHT = 250;

  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleStyleChange = (style, value) => {
    let updates = {};

    switch (style) {
      case 'bold':
        updates = { bold: !field.bold };
        break;

      case 'italic':
        updates = { italic: !field.italic };
        break;

      case 'underline':
        updates = { underline: !field.underline };
        break;

      case 'fontFamily':
        updates = { fontFamily: value };
        break;

      case 'fontSize':
        updates = { fontSize: value };
        break;

      case 'bulletList': {
        const bulletText = !field.value.startsWith('• ') ? '• ' + field.value : field.value.replace(/^• /, '');
        updates = { value: bulletText };
        break;
      }
      case 'numberList': {
        const lines = field.value.split('\n');
        const isNumbered = lines[0].match(/^\d+\. /);

        const numberedText = isNumbered
          ? lines.map((line) => line.replace(/^\d+\. /, '')).join('\n')
          : lines.map((line, i) => `${i + 1}. ${line}`).join('\n');

        updates = { value: numberedText };
        break;
      }

      default:
        break;
    }

    // Update the field with new styles
    onFieldChange(field.id, updates);
  };

  useEffect(() => {
    if (fieldRef.current && isActive) {
      const rect = fieldRef.current.getBoundingClientRect();
      field.boundingBox = {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height,
      };
    }
  }, [isActive, field.x, field.y, field.width]);
  const handleFieldClick = (e, fieldId) => {
    e.stopPropagation();
    onSelect(fieldId);
  };

  const handleMouseDown = (e, direction) => {
    if (isEditing) return;

    if (direction) {
      e.stopPropagation();
      setIsResizing(true);
      setResizeDirection(direction);
      initialWidth.current = field.width || 100;
      initialHeight.current = field.height || 30;
      initialMousePos.current = { x: e.clientX, y: e.clientY };
    } else {
      setIsDragging(true);
      initialMousePos.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        fieldX: field.x,
        fieldY: field.y,
      };
    }
  };
  const handleMouseMove = (e) => {
    if (!isDragging && !isResizing) return;

    if (isResizing) {
      const dx = (e.clientX - initialMousePos.current.x) / scale;
      const dy = (e.clientY - initialMousePos.current.y) / scale;

      let newWidth;
      let newHeight;
      let newX = field.x;
      let newY = field.y;
      // Handle horizontal resizing
      if (resizeDirection.includes('e')) {
        newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, initialWidth.current + dx));
      } else if (resizeDirection.includes('w')) {
        newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, initialWidth.current - dx));
        newX = field.x + (initialWidth.current - newWidth);
      }

      // Handle vertical resizing
      if (resizeDirection.includes('s')) {
        newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, initialHeight.current + dy));
      } else if (resizeDirection.includes('n')) {
        newHeight = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, initialHeight.current - dy));
        newY = field.y + (initialHeight.current - newHeight);
      }

      onResize(field.id, newWidth, newHeight, newX, newY);
    } else {
      const dx = (e.clientX - initialMousePos.current.mouseX) / scale;
      const dy = (e.clientY - initialMousePos.current.mouseY) / scale;

      const newX = initialMousePos.current.fieldX + dx;
      const newY = initialMousePos.current.fieldY + dy;

      onDragEnd(field.id, { x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing]);

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    onRemove(field.id);
  };

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'; // Reset height
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set to scroll height

      // Update field height in parent
      onResize(field.id, field.width, textareaRef.current.scrollHeight, field.x, field.y);
    }
  };

  // Add useEffect to adjust height when value changes
  useEffect(() => {
    if (isActive && field.templateId === 'template-textbox') {
      adjustTextareaHeight();
    }
  }, [field.value, isActive]);

  // Add this useEffect to show popover when field becomes active
  useEffect(() => {
    if (isActive && textareaRef.current && !anchorEl) {
      setAnchorEl(textareaRef.current);
    }
    if (!isActive && anchorEl) {
      setAnchorEl(null);
    }
  }, [isActive]);

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    if (field.templateId === 'template-textbox') {
      setIsEditing(true);
      onSelect(field.id);
      if (textareaRef.current) {
        setAnchorEl(textareaRef.current);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isEditing && fieldRef.current && !fieldRef.current.contains(e.target)) {
        setIsEditing(false);
        setAnchorEl(null);
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);

  const renderFieldContent = () => {
    switch (field.templateId) {
      case 'template-textbox':
        return isEditing ? (
          <>
            <textarea
              value={field.value || ''}
              ref={textareaRef}
              onChange={(e) => updateTextFieldValue(field.id, e.target.value)}
              className="pdf-field-input"
              autoFocus
              placeholder="Textbox"
              style={{
                width: field.width,
                height: field.height,
                fontFamily: field.fontFamily,
                fontSize: `${field.fontSize}px`,
                fontWeight: field.bold ? 'bold' : 'normal',
                fontStyle: field.italic ? 'italic' : 'normal',
                textDecoration: field.underline ? 'underline' : 'none',
              }}
            />
            <RichTextEditorForContract
              anchorEl={anchorEl}
              onClose={() => {
                setAnchorEl(null);
                setIsEditing(false);
              }}
              onDelete={() => handleRemoveClick(field.id)}
              onStyleChange={handleStyleChange}
              isEditing={isEditing}
            />
          </>
        ) : (
          <div
            className="pdf-field-text"
            style={{
              width: field.width,
              height: field.height,
              fontFamily: field.fontFamily,
              fontSize: `${field.fontSize}px`,
              fontWeight: field.bold ? 'bold' : 'normal',
              fontStyle: field.italic ? 'italic' : 'normal',
              textDecoration: field.underline ? 'underline' : 'none',
            }}>
            {field.value || field.label}
          </div>
        );
      default:
        return (
          <div className="field-content">
            <span className="field-icon">{getIconByKey(field.icon)}</span>
            <span className="field-label">{field.label}</span>
          </div>
        );
    }
  };

  const fieldStyle = {
    position: 'absolute',
    left: field.x,
    top: field.y,
    width: field.width,
    height: field.height,
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    zIndex: isActive ? 100 : 10,
  };

  return (
    <div
      ref={fieldRef}
      onMouseDown={handleMouseDown}
      onClick={(e) => handleFieldClick(e, field.id)}
      onDoubleClick={handleDoubleClick}
      className={`dropped-field ${isDragging ? 'dragging' : ''} ${isActive ? 'selected' : ''} ${
        isEditing ? 'editing' : ''
      }`}
      style={fieldStyle}>
      {renderFieldContent()}

      {isActive && (
        <>
          <div className="field-actions">
            <button onClick={handleRemoveClick} className="remove-button">
              ×
            </button>
          </div>
          <div className="resize-handles">
            <div
              className="resize-handle resize-handle-nw"
              onMouseDown={(e) => handleMouseDown(e, 'nw')}
              data-direction="nw"
            />
            <div
              className="resize-handle resize-handle-ne"
              onMouseDown={(e) => handleMouseDown(e, 'ne')}
              data-direction="ne"
            />
            <div
              className="resize-handle resize-handle-se"
              onMouseDown={(e) => handleMouseDown(e, 'se')}
              data-direction="se"
            />
            <div
              className="resize-handle resize-handle-sw"
              onMouseDown={(e) => handleMouseDown(e, 'sw')}
              data-direction="sw"
            />
          </div>
        </>
      )}
    </div>
  );
};

const PdfViewer = ({
  file,
  fields,
  onFieldDrop,
  onFieldDragEnd,
  onFieldRemove,
  onFieldResize,
  pageInputRef,
  updateTextFieldValue,
  onFieldChange,
}) => {
  const { currentPage, isEditingPage } = useSelector((state) => state.customContract);
  const dispatch = useDispatch();
  const [scale] = useState(1.2);
  const [thumbnails, setThumbnails] = useState([]);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const containerRef = useRef(null);
  const pageRef = useRef(null);

  const thumbnailsRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [pageWidth, setPageWidth] = useState(0);

  function onDocumentLoadSuccess({ numPages }) {
    dispatch(setNumPages(numPages));
    setThumbnails(Array.from({ length: numPages }, (_, i) => i + 1));
    setIsLoading(false);
  }

  useEffect(() => {
    if (isEditingPage && pageInputRef.current) {
      pageInputRef.current.focus();
      pageInputRef.current.select();
    }
  }, [isEditingPage]);

  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  const handleDrop = (e) => {
    e.preventDefault();
    const pageRect = pageRef.current.getBoundingClientRect();

    try {
      const fieldData = JSON.parse(e.dataTransfer.getData('field'));
      const { dragOffset } = fieldData;

      // Calculate position accounting for the initial drag offset
      const x = e.clientX - pageRect.left - (dragOffset?.x || 0);
      const y = e.clientY - pageRect.top - (dragOffset?.y || 0);

      onFieldDrop(fieldData, { x, y }, currentPage);
    } catch (error) {
      console.error('Error parsing dropped field data:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleContainerClick = (e) => {
    if (e.target !== containerRef.current || e.target.classList.contains('pdf-page')) {
      setActiveFieldId(null);
    }
  };

  useEffect(() => {
    if (thumbnailsRef.current) {
      const thumbnailElement = thumbnailsRef.current.querySelector(`.thumbnail:nth-child(${currentPage})`);
      if (thumbnailElement) {
        thumbnailElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [currentPage]);

  useEffect(() => {
    const a4Width = 595.28 * scale;
    setPageWidth(a4Width);
  }, [scale]);

  // Add keyboard event handler for deleting fields
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeFieldId && (e.key === 'Delete' || e.key === 'Backspace')) {
        onFieldRemove(activeFieldId);
        setActiveFieldId(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeFieldId, onFieldRemove]);

  return (
    <div className="custom-pdf-viewer">
      <div className="pdf-thumbnails" ref={thumbnailsRef}>
        {thumbnails.map((pageNum) => (
          <div
            key={pageNum}
            className={`thumbnail ${currentPage === pageNum ? 'active' : ''}`}
            onClick={() => dispatch(setCurrentPage(pageNum))}>
            <Document file={file}>
              <Page pageNumber={pageNum} width={150} renderTextLayer={false} renderAnnotationLayer={false} />
            </Document>
          </div>
        ))}
      </div>

      <div className="pdf-main-content">
        <div className="pdf-content" ref={containerRef} onClick={handleContainerClick}>
          <div className="pdf-container" onDrop={handleDrop} onDragOver={handleDragOver} ref={pageRef}>
            {isLoading && (
              <div
                style={{
                  marginTop: '108px',
                  textAlign: 'center',
                  width: `${pageWidth}px`,
                }}>
                <SaralLoaderImage />
              </div>
            )}
            <Document file={file} onLoadSuccess={onDocumentLoadSuccess}>
              <Page pageNumber={currentPage} scale={scale} renderTextLayer={true} renderAnnotationLayer={true} />
              {fields
                .filter((field) => field.page === currentPage)
                .map((field) => (
                  <DroppedField
                    key={field.id}
                    field={field}
                    scale={scale}
                    onDragEnd={onFieldDragEnd}
                    onRemove={onFieldRemove}
                    onResize={onFieldResize}
                    isActive={field.id === activeFieldId}
                    onSelect={setActiveFieldId}
                    updateTextFieldValue={updateTextFieldValue}
                    onFieldChange={onFieldChange}
                  />
                ))}
            </Document>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfViewer;
